import React, { useState, useRef, useEffect } from 'react';
import { Users, Plus, Minus, ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const PassengerSelector = ({ passengers, setPassengers }) => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updatePassengers = (type, delta) => {
    const newPassengers = { ...passengers };
    const newValue = newPassengers[type] + delta;

    if (type === 'adults') {
      // Min 1 adult, Max 9 adults
      if (newValue < 1 || newValue > 9) return;
      newPassengers.adults = newValue;
      // If reducing adults, check infants constraint (1 infant per adult max)
      if (newPassengers.infants > newPassengers.adults) {
        newPassengers.infants = newPassengers.adults;
      }
    } else if (type === 'children') {
      // Min 0, Max reasonable (e.g., 9)
      if (newValue < 0 || newValue > 9) return;
      newPassengers.children = newValue;
    } else if (type === 'infants') {
      // Min 0, Max = number of adults
      if (newValue < 0 || newValue > newPassengers.adults) return;
      newPassengers.infants = newValue;
    }

    setPassengers(newPassengers);
  };

  const totalPassengers = passengers.adults + passengers.children + passengers.infants;

  const getPassengerSummary = () => {
    const parts = [];
    if (passengers.adults > 0) {
      parts.push(`${passengers.adults} ${passengers.adults === 1 ? t('adults').slice(0, -1) : t('adults')}`);
    }
    if (passengers.children > 0) {
      parts.push(`${passengers.children} ${passengers.children === 1 ? t('children').slice(0, -1) : t('children')}`);
    }
    if (passengers.infants > 0) {
      parts.push(`${passengers.infants} ${passengers.infants === 1 ? t('infants').slice(0, -1) : t('infants')}`);
    }
    return parts.join(', ');
  };

  const CounterRow = ({ type, label, description, value, canDecrease, canIncrease }) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
      <div className="flex-1">
        <div className="font-semibold text-gray-900">{label}</div>
        <div className="text-sm text-gray-500">{description}</div>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => updatePassengers(type, -1)}
          disabled={!canDecrease}
          className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
            canDecrease
              ? 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          data-testid={`decrease-${type}-btn`}
        >
          <Minus size={16} />
        </button>
        <span className="w-8 text-center font-bold text-lg" data-testid={`${type}-count`}>
          {value}
        </span>
        <button
          type="button"
          onClick={() => updatePassengers(type, 1)}
          disabled={!canIncrease}
          className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
            canIncrease
              ? 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'
              : 'border-gray-200 text-gray-300 cursor-not-allowed'
          }`}
          data-testid={`increase-${type}-btn`}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        {t('passengers')} *
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-left relative bg-white hover:border-blue-500"
        data-testid="passenger-selector-btn"
      >
        <Users className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <span className="text-gray-900">
          {getPassengerSummary()}
        </span>
        <ChevronDown
          className={`absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          size={20}
        />
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-200 p-4 animate-fade-in">
          <CounterRow
            type="adults"
            label={t('adults')}
            description={t('adultsDesc')}
            value={passengers.adults}
            canDecrease={passengers.adults > 1}
            canIncrease={passengers.adults < 9}
          />
          <CounterRow
            type="children"
            label={t('children')}
            description={t('childrenDesc')}
            value={passengers.children}
            canDecrease={passengers.children > 0}
            canIncrease={passengers.children < 9}
          />
          <CounterRow
            type="infants"
            label={t('infants')}
            description={t('infantsDesc')}
            value={passengers.infants}
            canDecrease={passengers.infants > 0}
            canIncrease={passengers.infants < passengers.adults}
          />

          {/* Info message */}
          <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-700">
            <p>• {t('childrenNeedAdultError')}</p>
            <p>• 1 {t('infants').toLowerCase()} max / {t('adults').toLowerCase().slice(0, -1)}</p>
          </div>

          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="w-full mt-3 bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
            data-testid="confirm-passengers-btn"
          >
            OK
          </button>
        </div>
      )}
    </div>
  );
};

export default PassengerSelector;
